/**
 * Replay system: deterministic input recording + playback.
 *
 * Determinism rests on three legs:
 *  1. Fixed 60Hz physics timestep (already present in GameScene).
 *  2. All gameplay randomness routed through a seeded mulberry32 RNG
 *     instead of Math.random(); the seed is part of the replay header.
 *  3. Player inputs are recorded as (frame, kind, x, y) tuples and
 *     dispatched at the start of the matching physics substep on playback.
 *
 * The text format is deliberately line-based and human readable so
 * speed-runners can hand-edit demos and so files can be pasted into
 * chat / forums without binary encoding.
 *
 *     SHKR1 level=01 seed=1234567 frames=2400 result=won
 *     D 10 102.50,200.00
 *     U 45
 *     D 120 150.00,180.00
 *     U 200
 *
 * Coords are in world units (the same units onDown receives). Frames
 * count fixed-timestep substeps from 0.
 */

export type ReplayInputKind = 'D' | 'U';

export interface ReplayInput {
  /** 0-based fixed-step frame index when the input fires. */
  frame: number;
  kind: ReplayInputKind;
  /** World-space coordinates (only meaningful for 'D'). */
  x?: number;
  y?: number;
}

export interface Replay {
  /** Format/version tag, currently "SHKR1". */
  version: string;
  levelId: string;
  /** uint32 seed for the gameplay RNG. */
  seed: number;
  /** Number of fixed-timestep frames the original run took. */
  frames: number;
  /** 'won' | 'dead' | 'open' (recorded but never finished). */
  result: 'won' | 'dead' | 'open';
  inputs: ReplayInput[];
}

/** Mulberry32 — tiny seeded PRNG. Identical output across browsers/Node. */
export function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return function () {
    a = (a + 0x6D2B79F5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Pick a fresh uint32 seed from Math.random(). Replays save this seed
 *  so future playbacks are deterministic regardless of host RNG. */
export function pickSeed(): number {
  return (Math.random() * 0xffffffff) >>> 0;
}

export function encodeReplay(r: Replay): string {
  const lines: string[] = [];
  lines.push(
    `SHKR1 level=${r.levelId} seed=${r.seed >>> 0} frames=${r.frames} result=${r.result}`,
  );
  for (const ev of r.inputs) {
    if (ev.kind === 'D') {
      // Two decimals is plenty: anchor hit-test radius is several units wide.
      const x = (ev.x ?? 0).toFixed(2);
      const y = (ev.y ?? 0).toFixed(2);
      lines.push(`D ${ev.frame} ${x},${y}`);
    } else {
      lines.push(`U ${ev.frame}`);
    }
  }
  // Trailing newline keeps tools like `cat` tidy.
  return lines.join('\n') + '\n';
}

export function parseReplay(text: string): Replay | null {
  const lines = text.split(/\r?\n/).map((l) => l.trim()).filter((l) => l.length > 0);
  if (lines.length === 0) return null;
  const header = lines[0]!;
  if (!header.startsWith('SHKR1 ')) return null;
  const meta: Record<string, string> = {};
  for (const tok of header.slice(6).split(/\s+/)) {
    const eq = tok.indexOf('=');
    if (eq <= 0) continue;
    meta[tok.slice(0, eq)] = tok.slice(eq + 1);
  }
  const levelId = meta['level'];
  const seedStr = meta['seed'];
  const framesStr = meta['frames'];
  const result = (meta['result'] ?? 'open') as Replay['result'];
  if (!levelId || !seedStr || !framesStr) return null;
  const seed = parseInt(seedStr, 10);
  const frames = parseInt(framesStr, 10);
  if (!Number.isFinite(seed) || !Number.isFinite(frames)) return null;

  const inputs: ReplayInput[] = [];
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i]!;
    if (line.startsWith('#')) continue; // comments
    const parts = line.split(/\s+/);
    const kind = parts[0];
    if (kind === 'D') {
      const frame = parseInt(parts[1] ?? '', 10);
      const xy = (parts[2] ?? '').split(',');
      const x = parseFloat(xy[0] ?? '');
      const y = parseFloat(xy[1] ?? '');
      if (!Number.isFinite(frame) || !Number.isFinite(x) || !Number.isFinite(y)) continue;
      inputs.push({ frame, kind: 'D', x, y });
    } else if (kind === 'U') {
      const frame = parseInt(parts[1] ?? '', 10);
      if (!Number.isFinite(frame)) continue;
      inputs.push({ frame, kind: 'U' });
    }
  }
  // Stable sort by frame in case a hand-edited file is out of order.
  inputs.sort((a, b) => a.frame - b.frame);

  return { version: 'SHKR1', levelId, seed: seed >>> 0, frames, result, inputs };
}
